import { useEffect, useRef, useState } from "react";
const homeAsset = (assetPath) => `${process.env.PUBLIC_URL || ""}${assetPath}`;

const Cloud = ({
  top,
  left = 50,
  distance,
  direction = "left",
  variant = 1,
  zIndex = 5,
  delay = 0,
  duration,
  pauseAway = 200,
  pauseHome = 2000,
  moveX,
  moveY,
}) => {
  const [isMovedAway, setIsMovedAway] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  );
  const timeoutsRef = useRef([]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const addTimeout = (fn, ms) => {
    const id = setTimeout(() => {
      timeoutsRef.current = timeoutsRef.current.filter((t) => t !== id);
      fn();
    }, ms);
    timeoutsRef.current.push(id);
    return id;
  };

  // Preserve exact original movement speed: 1500px over 8s = 187.5 px/s
  const baseSpeed = 1500 / 8;

  // Rendered cloud width according to variant and viewport scale
  const cloudBaseWidth = variant === 1 ? 247 : variant === 2 ? 186 : 213;
  const isMobileOrTablet = windowWidth <= 1024;
  const scale = isMobileOrTablet ? 0.95 : 1.3;

  // Turn around just 10px past the screen edge so invisible time is reduced by 3s down to ~1.5s
  const calculateDistance = () => {
    if (typeof distance === "number") {
      return distance;
    }

    const startX = (windowWidth * (typeof left === "number" ? left : parseFloat(left) || 50)) / 100;
    const visibleWidth = Math.ceil(cloudBaseWidth * ((scale + 1) / 2));
    if (direction === "left") {
      return Math.ceil(startX + visibleWidth + 10);
    } else {
      return Math.ceil(windowWidth - startX + 10);
    }
  };

  const currentDistance = calculateDistance();
  const defaultX = direction === "left" ? -currentDistance : currentDistance;

  let defaultY = 0;
  if (variant === 1) defaultY = -50;
  else if (variant === 3) defaultY = 100;

  const targetX = moveX ?? defaultX;
  const targetY = moveY ?? defaultY;

  // Shared travel duration across all clouds in the section so they stay in 100% lockstep sync
  const referenceSectionDistance = Math.ceil(windowWidth * 0.55 + 240 * ((scale + 1) / 2) + 10);
  const sharedDuration = Math.max(1.5, +(referenceSectionDistance / baseSpeed).toFixed(2));
  const effectiveDuration = duration !== undefined ? duration : sharedDuration;

  useEffect(() => {
    const initialDelay = variant * 200 + delay;

    const animationCycle = () => {
      setIsMovedAway(true);

      addTimeout(() => {
        setIsMovedAway(false);
        addTimeout(animationCycle, effectiveDuration * 1000 + pauseHome);
      }, effectiveDuration * 1000 + pauseAway);
    };

    addTimeout(animationCycle, initialDelay);

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [variant, delay, effectiveDuration, pauseAway, pauseHome]);

  const cloudStyle = {
    position: "absolute",
    top: `${top}px`,
    left: `${left}%`,
    transform: isMovedAway
      ? `translateX(${targetX}px) translateY(${targetY}px)`
      : `translateX(0) translateY(0)`,
    transition: `transform ${effectiveDuration}s ease`,
    zIndex,
  };


  // Render the appropriate cloud based on the variant
  const renderCloud = () => {
    switch (variant) {
      case 1:
        return (
          <svg
            width="247"
            height="128"
            viewBox="0 0 247 128"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <g filter="url(#filter0_d_2872_666)">
              <rect
                x="0.652344"
                width="237.929"
                height="120.007"
                fill="url(#pattern0_cloud1)"
                shapeRendering="crispEdges"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_2872_666"
                x="0.652344"
                y="0"
                width="245.929"
                height="128.007"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="4" />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                <feComposite in2="color" operator="in" />
                <feComposite in2="BackgroundImageFix" operator="over" result="effect1_dropShadow_2872_666" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2872_666" result="shape" />
                {/* Invert the final blended shape to ensure consistent color across browsers */}
                <feColorMatrix
                  in="shape"
                  type="matrix"
                  values="-1 0 0 0 1 0 -1 0 0 1 0 0 -1 0 1 0 0 0 1 0"
                  result="finalShape"
                  colorInterpolationFilters="sRGB"
                />
              </filter>
              <pattern id="pattern0_cloud1" patternContentUnits="objectBoundingBox" width="1" height="1">
                <use xlinkHref="#image0_cloud1" transform="scale(0.000770416 0.00138889)" />
              </pattern>
              <image id="image0_cloud1" width="1298" height="720" preserveAspectRatio="none" xlinkHref={homeAsset("/generated-home-assets/home-asset-097.webp")} />
            </defs>
          </svg>
        );
      case 2:
        return (
          <svg
            width="186"
            height="78"
            viewBox="0 0 186 78"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <g filter="url(#filter0_d_2872_666)">
              <rect
                y="0.098877"
                width="185.379"
                height="77.7427"
                fill="url(#pattern0_cloud2)"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_2872_666"
                x="0.652344"
                y="0"
                width="245.929"
                height="128.007"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="4" />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                <feComposite in2="color" operator="in" />
                <feComposite in2="BackgroundImageFix" operator="over" result="effect1_dropShadow_2872_666" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2872_666" result="shape" />
                {/* Invert the final blended shape to ensure consistent color across browsers */}
                <feColorMatrix
                  in="shape"
                  type="matrix"
                  values="-1 0 0 0 1 0 -1 0 0 1 0 0 -1 0 1 0 0 0 1 0"
                  result="finalShape"
                  colorInterpolationFilters="sRGB"
                />
              </filter>
              <pattern id="pattern0_cloud2" patternContentUnits="objectBoundingBox" width="1" height="1">
                <use xlinkHref="#image0_cloud2" transform="scale(0.00104603 0.00249377)" />
              </pattern>
              <image id="image0_cloud2" width="956" height="401" preserveAspectRatio="none" xlinkHref={homeAsset("/generated-home-assets/home-asset-098.webp")} />
            </defs>
          </svg>
        );

      case 3:
        // Third cloud structure
        return (
          <svg
            width="213"
            height="116"
            viewBox="0 0 213 116"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <g filter="url(#filter0_d_2872_666)">
              <rect
                x="0.4375"
                y="0.802002"
                width="211.945"
                height="114.966"
                fill="url(#pattern0_cloud3)"
              />
            </g>
            <defs>
              <filter
                id="filter0_d_2872_666"
                x="0.652344"
                y="0"
                width="245.929"
                height="128.007"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="4" />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                <feComposite in2="color" operator="in" />
                <feComposite in2="BackgroundImageFix" operator="over" result="effect1_dropShadow_2872_666" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2872_666" result="shape" />
                {/* Invert the final blended shape to ensure consistent color across browsers */}
                <feColorMatrix
                  in="shape"
                  type="matrix"
                  values="-1 0 0 0 1 0 -1 0 0 1 0 0 -1 0 1 0 0 0 1 0"
                  result="finalShape"
                  colorInterpolationFilters="sRGB"
                />
              </filter>
              <pattern id="pattern0_cloud3" patternContentUnits="objectBoundingBox" width="1" height="1">
                <use xlinkHref="#image0_cloud3" transform="scale(0.000914913 0.00168634)" />
              </pattern>
              <image id="image0_cloud3" width="1093" height="593" preserveAspectRatio="none" xlinkHref={homeAsset("/generated-home-assets/home-asset-098.webp")} />
            </defs>
          </svg>
        );

      default:
        return null;
    }
  };

  return (
    <div className="animated-cloud" style={cloudStyle}>
      {renderCloud()}
    </div>
  );
};

export default Cloud;